import React, { useState } from "react";

import { Box, Button, Dialog, Select, Label, TextInput } from "@blasterjs/core";

import { DialogBody, DialogFooter, DialogHeader } from "./DialogLayout";

import { useAppDispatch, useAppSelector } from "../hooks";
import {
  CaseRowOptions,
  DialogMode,
  archiveCase,
  closeOpenImageManagementCaseDialogParent,
  putCaseOnHold,
  revertCaseStatus,
  removeCaseHold
} from "../slices/ImageManagementCaseDialogParent";
import { CaseStatus, formatCaseStatus } from "../models";
import { UserFeedbackMessage } from "./ImageManagementImageDialogParent";
import { fetchImageManagementImagesCaseResults } from "../slices/imageManagement";

const ImageManagementCaseDialogParent = () => {
  const dispatch = useAppDispatch();

  const dialogMode = useAppSelector(state => state.imageManagementCaseDialogParent.mode);
  const modalParams = useAppSelector(state => state.imageManagementCaseDialogParent.modalParams);

  const closeDialog = () => {
    dispatch(closeOpenImageManagementCaseDialogParent());
  };

  const childControl = () => {
    switch (modalParams?.caseRowOption) {
      case CaseRowOptions.RevertStatus:
        return <RevertStatusControl />;
      case CaseRowOptions.PutOnHold:
        return <PutOnHoldControl />;
      case CaseRowOptions.RemoveHold:
        return <RemoveHoldControl />;
      case CaseRowOptions.Download:
        return <DownloadImages />;
      case CaseRowOptions.Archive:
        return <ArchiveControl />;
      default:
        return <div>-</div>;
    }
  };

  return (
    <Dialog
      isOpen={dialogMode === DialogMode.OpenForEdit}
      onRequestClose={closeDialog}
      appElementId="root"
      width="600px"
    >
      {childControl()}
    </Dialog>
  );
};

export default ImageManagementCaseDialogParent;

/*
  child controls that will conditionally render in the parent dialog
*/

const RevertStatusControl = () => {
  const dispatch = useAppDispatch();

  const [reasonForChange, setReasonForChange] = useState("");
  const [selectedCaseStatus, setSelectedCaseStatus] = useState<CaseStatus | null>(null);

  const modalParams = useAppSelector(state => state.imageManagementCaseDialogParent.modalParams);
  const modalMessage = useAppSelector(state => state.imageManagementCaseDialogParent.modalMessage);

  const caseId = modalParams && "caseId" in modalParams ? modalParams.caseId : "";

  const closeDialog = () => {
    dispatch(closeOpenImageManagementCaseDialogParent());
  };

  const onSave = () => {
    if (caseId && selectedCaseStatus && reasonForChange.length > 0) {
      dispatch(
        revertCaseStatus({
          caseId: caseId,
          caseStatus: selectedCaseStatus,
          reasonForChange: reasonForChange
        })
      );
    }
  };

  const onSelectStatus = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedCaseStatus(event.currentTarget.value as CaseStatus);
  };

  const reasonForChangeOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReasonForChange(event.currentTarget.value);
  };

  const title = "Revert Case Status";

  const previousStatuses: CaseStatus[] = modalParams
    ? "contextInfo" in modalParams
      ? (modalParams.contextInfo as CaseStatus[])
      : []
    : [];

  return (
    <>
      <DialogHeader title={title} closeDialog={closeDialog} />
      <DialogBody>
        <Box display="block" justifyContent="space-between" mb={2}>
          <Box style={{ width: "100%" }}>
            <Label>Previous Case Statuses</Label>
            <Select onChange={onSelectStatus} defaultValue={""}>
              <option key="" value="">
                What status would you like to revert the following case to?
              </option>
              {previousStatuses.map(status => {
                return (
                  <option key={status} value={status}>
                    {formatCaseStatus(status)}
                  </option>
                );
              })}
            </Select>
          </Box>
          <Box style={{ width: "100%" }}>
            <Label>Reason for Change</Label>
            <TextInput
              placeholder={"Enter reason"}
              value={reasonForChange}
              onChange={reasonForChangeOnChange}
              style={{ paddingLeft: "1rem" }}
            />
          </Box>
          {modalMessage && (
            <UserFeedbackMessage message={modalMessage.message} success={modalMessage.success} />
          )}
        </Box>
      </DialogBody>
      <DialogFooter>
        {!modalMessage && (
          <Button intent="primary" appearance="prominent" onClick={onSave}>
            Revert Status
          </Button>
        )}
        <Button onClick={closeDialog}>{!modalMessage ? "Cancel" : "Ok"}</Button>
      </DialogFooter>
    </>
  );
};

const PutOnHoldControl = () => {
  const dispatch = useAppDispatch();

  const [reasonForChange, setReasonForChange] = useState("");

  const modalParams = useAppSelector(state => state.imageManagementCaseDialogParent.modalParams);
  const modalMessage = useAppSelector(state => state.imageManagementCaseDialogParent.modalMessage);
  const caseId = modalParams && "caseId" in modalParams ? modalParams.caseId : "";
  const caseHisto = modalParams && "contextInfo" in modalParams ? modalParams.contextInfo : "";
  const caseEndo = modalParams && "contextInfo2" in modalParams ? modalParams.contextInfo2 : "";

  const closeDialog = () => {
    dispatch(closeOpenImageManagementCaseDialogParent());
  };

  const reasonForChangeOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReasonForChange(event.currentTarget.value);
  };

  const onSave = () => {
    if (caseId && reasonForChange.length > 0) {
      dispatch(putCaseOnHold({ caseId: caseId, reasonForChange: reasonForChange })).then(() => {
        dispatch(fetchImageManagementImagesCaseResults());
      });
    }
  };

  return (
    <>
      <DialogHeader title="Put on Hold" closeDialog={closeDialog} />
      <DialogBody>
        <Box style={{ width: "100%" }}>
          Are you sure you want to put the following case on hold?
        </Box>
        <Box style={{ width: "100%" }}>All images in the case will also be put on hold. </Box>
        <br />
        {caseHisto && <Box style={{ width: "100%" }}>Histo Procedure ID: {caseHisto} </Box>}
        {caseEndo && <Box style={{ width: "100%" }}>Endo Procedure ID: {caseEndo}</Box>}
        <br />
        <Box display="block" justifyContent="space-between" mb={2}>
          <Box style={{ width: "100%" }}>
            <Label>Reason for Change*</Label>
            <TextInput
              placeholder={"Enter reason"}
              value={reasonForChange}
              onChange={reasonForChangeOnChange}
              style={{ paddingLeft: "1rem" }}
            />
          </Box>
          {modalMessage && (
            <UserFeedbackMessage message={modalMessage.message} success={modalMessage.success} />
          )}
        </Box>
      </DialogBody>
      <DialogFooter>
        {!modalMessage && (
          <Button intent="primary" appearance="prominent" onClick={onSave}>
            Confirm
          </Button>
        )}
        <Button onClick={closeDialog}>Cancel</Button>
      </DialogFooter>
    </>
  );
};

const RemoveHoldControl = () => {
  const dispatch = useAppDispatch();

  const [reasonForChange, setReasonForChange] = useState("");

  const modalParams = useAppSelector(state => state.imageManagementCaseDialogParent.modalParams);
  const modalMessage = useAppSelector(state => state.imageManagementCaseDialogParent.modalMessage);
  const caseId = modalParams && "caseId" in modalParams ? modalParams.caseId : "";
  const caseHisto = modalParams && "contextInfo" in modalParams ? modalParams.contextInfo : "";
  const caseEndo = modalParams && "contextInfo2" in modalParams ? modalParams.contextInfo2 : "";

  const closeDialog = () => {
    dispatch(closeOpenImageManagementCaseDialogParent());
  };

  const reasonForChangeOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReasonForChange(event.currentTarget.value);
  };

  const onSave = () => {
    if (caseId && reasonForChange.length > 0) {
      dispatch(removeCaseHold({ caseId: caseId, reasonForChange: reasonForChange })).then(() => {
        dispatch(fetchImageManagementImagesCaseResults());
      });
    }
  };

  return (
    <>
      <DialogHeader title="Remove Hold" closeDialog={closeDialog} />
      <DialogBody>
        <Box style={{ width: "100%" }}>
          Are you sure you want to remove the hold on the following case?
        </Box>
        <Box style={{ width: "100%" }}>The hold will be removed from all images in the case.</Box>
        <br />
        {caseHisto && <Box style={{ width: "100%" }}>Histo Procedure ID: {caseHisto} </Box>}
        {caseEndo && <Box style={{ width: "100%" }}>Endo Procedure ID: {caseEndo}</Box>}
        <br />
        <Box display="block" justifyContent="space-between" mb={2}>
          <Box style={{ width: "100%" }}>
            <Label>Reason for Change</Label>
            <TextInput
              placeholder={"Enter reason"}
              value={reasonForChange}
              onChange={reasonForChangeOnChange}
              style={{ paddingLeft: "1rem" }}
            />
          </Box>
          {modalMessage && (
            <UserFeedbackMessage message={modalMessage.message} success={modalMessage.success} />
          )}
        </Box>
      </DialogBody>
      <DialogFooter>
        {!modalMessage && (
          <Button intent="primary" appearance="prominent" onClick={onSave}>
            Confirm
          </Button>
        )}
        <Button onClick={closeDialog}>Cancel</Button>
      </DialogFooter>
    </>
  );
};

const DownloadImages = () => {
  const dispatch = useAppDispatch();

  const closeDialog = () => {
    dispatch(closeOpenImageManagementCaseDialogParent());
  };

  const onSave = () => {
    //console.log("saving...");
  };

  const title = "Download images in Case";

  return (
    <>
      <DialogHeader title={title} closeDialog={closeDialog} />
      <DialogBody>
        <Box display="block" justifyContent="space-between" mb={2}>
          <Box style={{ width: "100%" }}>
            <Label>Are you sure you want to download the images in this case?</Label>
          </Box>
        </Box>
      </DialogBody>
      <DialogFooter>
        <Button intent="primary" appearance="prominent" onClick={onSave}>
          Download Case
        </Button>
        <Button onClick={closeDialog}>Cancel</Button>
      </DialogFooter>
    </>
  );
};

const ArchiveControl = () => {
  const dispatch = useAppDispatch();

  const modalParams = useAppSelector(state => state.imageManagementCaseDialogParent.modalParams);
  const modalMessage = useAppSelector(state => state.imageManagementCaseDialogParent.modalMessage);

  const [reasonForChange, setReasonForChange] = useState("");

  const caseId = modalParams && "caseId" in modalParams ? modalParams.caseId : "";
  const caseHisto = modalParams && "contextInfo" in modalParams ? modalParams.contextInfo : "";
  const caseEndo = modalParams && "contextInfo2" in modalParams ? modalParams.contextInfo2 : "";

  const closeDialog = () => {
    dispatch(closeOpenImageManagementCaseDialogParent());
  };

  const title = `Archive Case`;

  const reasonForChangeOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReasonForChange(event.currentTarget.value);
  };

  const confirmDisabled = !(caseId && reasonForChange.length > 0);

  const onSave = () => {
    if (caseId && reasonForChange.length > 0) {
      dispatch(archiveCase({ caseId: caseId, reasonForChange: reasonForChange })).then(() => {
        dispatch(fetchImageManagementImagesCaseResults());
      });
    }
  };

  return (
    <>
      <DialogHeader title={title} closeDialog={closeDialog} />
      <DialogBody>
        <Box style={{ width: "100%" }}>Are you sure you want to archive the following case?</Box>
        <Box style={{ width: "100%" }}>All images in the case will also be archived.</Box>
        <br />
        {caseHisto && <Box style={{ width: "100%" }}>Histo Procedure ID: {caseHisto} </Box>}
        {caseEndo && <Box style={{ width: "100%" }}>Endo Procedure ID: {caseEndo}</Box>}
        <br />
        <Box style={{ width: "100%" }}>
          <Label>Reason for Change*</Label>
          <TextInput
            placeholder={"Enter reason"}
            value={reasonForChange}
            disabled={modalMessage}
            onChange={reasonForChangeOnChange}
            style={{ paddingLeft: "1rem" }}
          />
        </Box>
        {modalMessage && (
          <UserFeedbackMessage message={modalMessage.message} success={modalMessage.success} />
        )}
      </DialogBody>
      <DialogFooter>
        {!modalMessage && (
          <Button
            disabled={confirmDisabled}
            appearance="prominent"
            intent="danger"
            onClick={onSave}
          >
            Confirm
          </Button>
        )}
        <Button onClick={closeDialog}>{!modalMessage ? "Cancel" : "Ok"}</Button>
      </DialogFooter>
    </>
  );
};
